import { FC, memo, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material"
import { Modal } from "@/core/components"
import { getApiErrorMessage } from "@/core/utils"
import { GuestBot } from "../../interfaces"
import { useCreateJoinJobMutation } from "../../store"
import { LINKS_MAX, validateCampaignInput } from "../../utils/campaignLinks"
import type { Notify } from "./notify"
import {
  serializeHistoryConsent,
  validateHistoryConsent,
} from "@/core/utils/historyConsent"
import { trafficPolicyErrorMessage } from "@/modules/userbot/components/common/trafficPolicy"

const STEPS = ["Ссылки", "Исполнитель"]

interface IProps {
  notify: Notify
  onClose: () => void
  /** Navigate to a join-job detail (create success AND 409 «Открыть вступление»). */
  onOpenJob: (jobId: string) => void
}

/**
 * Вся форма живёт в отдельном компоненте ВНУТРИ Modal: у общего `Modal`
 * (`core/components/modal.tsx`) содержимое — инлайновый элемент, поэтому
 * каждый ререндер обёртки перемонтировал бы диалог (вместе с вводом).
 * Обёртка — React.memo и получает стабилизированные (`useCallback`) пропсы.
 */
const WizardForm: FC<IProps> = ({ notify, onClose, onOpenJob }) => {
  const [step, setStep] = useState(0)
  const [linksText, setLinksText] = useState("")
  const [role, setRole] = useState("guest-bot")
  const [pauseMsBetweenLinks, setPauseMsBetweenLinks] = useState(30000)
  const [jitterMaxMs, setJitterMaxMs] = useState(3000)
  const [downloadMessages, setDownloadMessages] = useState(false)
  const [analyzeAfterJoin, setAnalyzeAfterJoin] = useState(false)
  const [showExtra, setShowExtra] = useState(false)
  const [submitError, setSubmitError] = useState<{
    message: string
    activeJoinJobId?: string
  } | null>(null)
  const [createJoinJob, { isLoading }] = useCreateJoinJobMutation()

  const validation = useMemo(
    () => validateCampaignInput(linksText),
    [linksText],
  )
  const linksCount = useMemo(
    () => linksText.split(/\r?\n/).filter((l) => l.trim() !== "").length,
    [linksText],
  )

  // Spec §5.4: errors AND duplicates block «Далее» (AC 5/6, E7) — a deliberate
  // divergence from v1, where duplicates were only marked.
  const consentErrors = validateHistoryConsent(
    downloadMessages,
    analyzeAfterJoin,
  )
  const stepValid = [
    validation.errors.length === 0 && validation.duplicateIndexes.length === 0,
    consentErrors.length === 0 &&
      role.trim().length >= 1 &&
      pauseMsBetweenLinks >= 0 &&
      pauseMsBetweenLinks <= 3_600_000 &&
      jitterMaxMs >= 0 &&
      jitterMaxMs <= 60_000,
  ][step]

  const goTo = (next: number) => {
    setSubmitError(null)
    setStep(next)
  }

  const handleSubmit = async () => {
    if (!stepValid) return
    setSubmitError(null)
    try {
      const consent = serializeHistoryConsent(
        downloadMessages,
        analyzeAfterJoin,
      )
      const res = await createJoinJob({
        body: {
          links: validation.links,
          role: role.trim(),
          pauseMsBetweenLinks,
          jitterMaxMs,
          downloadMessages: consent.downloadMessages,
          analyzeAfterJoin: consent.analyze,
        },
      }).unwrap()
      notify("success", "Вступление запущено")
      onClose()
      onOpenJob(res.id)
    } catch (e) {
      if (GuestBot.isActiveJoinJobConflict(e)) {
        setSubmitError({
          message: "У этой роли уже есть активное вступление",
          activeJoinJobId: e.data.activeJoinJobId,
        })
        return
      }
      const status = (e as { status?: number }).status
      if (status === 404) {
        setSubmitError({
          message: `Вступление не создано: нет доступного исполнителя для указанной роли.\n${getApiErrorMessage(e)}`,
        })
        return
      }
      if (status === 400) {
        const msg = trafficPolicyErrorMessage(e)
        setSubmitError({
          message: msg.startsWith("duplicate link:")
            ? `Дубликат ссылки: ${msg.slice("duplicate link:".length).trim()}`
            : msg,
        })
        return
      }
      setSubmitError({ message: getApiErrorMessage(e) })
    }
  }

  return (
    <Stack spacing={2}>
      <Stepper activeStep={step} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && (
        <Stack spacing={1}>
          <TextField
            label="Ссылки (по одной в строке)"
            multiline
            minRows={6}
            value={linksText}
            onChange={(e) => setLinksText(e.target.value)}
          />
          <Typography variant="body2" color="text.secondary">
            {linksCount} / {LINKS_MAX}
          </Typography>
          {validation.errors
            .filter((err) => err.index === -1)
            .map((err, i) => (
              <Alert key={i} severity="error">
                {err.message}
              </Alert>
            ))}
          <Box>
            {linksText.split(/\r?\n/).map((line, i) => {
              const trimmed = line.trim()
              if (!trimmed) return null
              const lineError = validation.errors.find((e) => e.index === i)
              const isDuplicate = validation.duplicateIndexes.includes(i)
              return (
                <Box key={i} display="flex" gap={1} alignItems="center">
                  <Typography variant="body2" noWrap sx={{ maxWidth: 420 }}>
                    {trimmed}
                  </Typography>
                  {isDuplicate && (
                    <Chip
                      size="small"
                      color="warning"
                      label="Дубликат ссылки"
                    />
                  )}
                  {lineError && (
                    <Typography variant="caption" color="error">
                      {lineError.message}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>
        </Stack>
      )}

      {step === 1 && (
        <Stack spacing={2}>
          <TextField
            label="Роль"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            helperText="По умолчанию guest-bot"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={downloadMessages}
                onChange={(e) => setDownloadMessages(e.target.checked)}
              />
            }
            label="Скачивать историю сообщений"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={analyzeAfterJoin}
                onChange={(e) => setAnalyzeAfterJoin(e.target.checked)}
              />
            }
            label="Анализировать чаты после вступления"
            sx={{ mt: -1 }}
          />
          {consentErrors.map((message) => (
            <Alert key={message} severity="error">
              {message}
            </Alert>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Скачивание истории и анализ — независимые разрешения, выключенные по
            умолчанию. Анализ требует явного разрешения скачивания истории.
          </Typography>
          <Box>
            <Button size="small" onClick={() => setShowExtra((v) => !v)}>
              {showExtra
                ? "Скрыть дополнительные настройки"
                : "Дополнительные настройки"}
            </Button>
            <Collapse in={showExtra}>
              <Stack spacing={2} mt={1}>
                <TextField
                  label="Пауза между ссылками, мс"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={pauseMsBetweenLinks}
                  onChange={(e) => {
                    // Пустой/частичный ввод не даёт NaN или «тихий» 0 —
                    // диапазон проверяет stepValid.
                    if (e.target.value === "") return
                    const next = Number(e.target.value)
                    if (Number.isNaN(next)) return
                    setPauseMsBetweenLinks(next)
                  }}
                  helperText="По умолчанию 30 000 мс. Диапазон 0 – 3 600 000."
                />
                <TextField
                  label="Максимальный джиттер, мс"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={jitterMaxMs}
                  onChange={(e) => {
                    if (e.target.value === "") return
                    const next = Number(e.target.value)
                    if (Number.isNaN(next)) return
                    setJitterMaxMs(next)
                  }}
                  helperText="По умолчанию 3 000 мс. Диапазон 0 – 60 000."
                />
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      )}

      {submitError && (
        <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
          {submitError.message}
          {submitError.activeJoinJobId && (
            <Box mt={1}>
              <Button
                size="small"
                variant="contained"
                onClick={() => onOpenJob(submitError.activeJoinJobId!)}
              >
                Открыть вступление
              </Button>
            </Box>
          )}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" mt={1}>
        <Button disabled={step === 0} onClick={() => goTo(step - 1)}>
          Назад
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!stepValid}
            onClick={() => goTo(step + 1)}
          >
            Далее
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!stepValid || isLoading}
            onClick={handleSubmit}
          >
            Запустить вступление
            {isLoading && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Button>
        )}
      </Box>
    </Stack>
  )
}

export const JoinWizard: FC<IProps> = memo(function JoinWizard(props) {
  const { onClose } = props

  return (
    <Modal title="Вступить в группы" onClose={onClose} disableBackdropClose>
      <WizardForm {...props} />
    </Modal>
  )
})
